<script setup lang="ts">
import { ref, computed, onMounted, watchEffect } from 'vue';
import api from '~/api';
import type { Face } from '~/types';

const props = defineProps<{
  targetMaterialId: string;
  targetMediaUrl: string;
  isUploading?: boolean;
}>();
const emit = defineEmits<{
  (e: 'result', payload: { url: string; type: 'image' | 'video' }): void;
  (e: 'state', s: { canGenerate: boolean; label: string; costLabel: string; busy: boolean }): void;
}>();

const authStore = useAuthStore();

const faces = ref<Face[]>([]);
const selectedFaceId = ref('');
const isUploadingFace = ref(false);
const submitting = ref(false);
const polling = ref(false);
const detectingTarget = ref(false);
const errorMsg = ref('');

const canGenerate = computed(() =>
  !submitting.value && !polling.value && !props.isUploading && !!props.targetMaterialId && !!selectedFaceId.value,
);

const statusText = computed(() => {
  if (detectingTarget.value) return 'Detecting…';
  if (submitting.value) return 'Submitting…';
  if (polling.value) return 'Swapping…';
  return 'Generate';
});

async function loadFaces() {
  if (!authStore.isLogin) return;
  try {
    const list: any = await api.getFaceList(1, 30);
    faces.value = Array.isArray(list) ? list : (list.lists ?? []);
    if (!selectedFaceId.value && faces.value.length) {
      selectedFaceId.value = faces.value[0].face_id;
    }
  } catch {/* ignore */}
}

onMounted(loadFaces);

async function onAddFace(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  isUploadingFace.value = true;
  errorMsg.value = '';
  try {
    const r: any = await api.uploadFace(file);
    if (r?.face_id) selectedFaceId.value = r.face_id;
    await loadFaces();
  } catch (e: any) {
    errorMsg.value = e?.message || 'Face upload failed';
  } finally {
    isUploadingFace.value = false;
    input.value = '';
  }
}

function pickFace(id: string) {
  selectedFaceId.value = id;
}

async function generate() {
  if (props.isUploading) {
    errorMsg.value = 'Image uploading, please wait...';
    return;
  }
  if (!canGenerate.value) return;
  errorMsg.value = '';
  submitting.value = true;
  try {
    detectingTarget.value = true;
    await api.uploadDetectCreate({ materialId: props.targetMaterialId });
    let detectOk = false;
    for (let i = 0; i < 20; i++) {
      const d: any = await api.uploadDetectResult(props.targetMaterialId);
      if (d.detect_status === 1) { detectOk = true; break; }
      if (d.detect_status === -1) throw new Error('detect failed');
      await new Promise((r) => setTimeout(r, 3000));
    }
    detectingTarget.value = false;
    if (!detectOk) throw new Error('detect timeout');

    const r = await api.createFaceSwap(
      props.targetMaterialId,
      [{ detected_face_url: '', user_face_id: selectedFaceId.value }],
      'P001',
      'single',
    );
    submitting.value = false;
    polling.value = true;

    let attempts = 0;
    const timer = setInterval(async () => {
      attempts++;
      try {
        const res = await api.fetchFaceSwapRes(r.order_id, r.task_id);
        if (res.task_status === 1) {
          const isVid = /\.(mp4|webm|mov)$/i.test(res.url);
          emit('result', { url: res.url, type: isVid ? 'video' : 'image' });
          clearInterval(timer); polling.value = false;
        } else if (res.task_status === -1 || attempts >= 30) {
          errorMsg.value = (res as any).message || 'Generation failed';
          clearInterval(timer); polling.value = false;
        }
      } catch {/* ignore */}
    }, 6000);
  } catch (e: any) {
    submitting.value = false;
    detectingTarget.value = false;
    errorMsg.value = e?.message || 'Submission failed';
  }
}

// 把生成按钮状态镜像给父级（editor.vue 在上传区下面/移动端底部放一个
// 同样触发生成的桥接按钮，逻辑仍全部留在本组件内）
watchEffect(() => {
  emit('state', {
    canGenerate: canGenerate.value,
    label: statusText.value,
    costLabel: '4+ Credits',
    busy: submitting.value || polling.value,
  });
});
defineExpose({ generate });
</script>

<template>
  <div class="panel">
    <header class="head">
      <h2>Face Swap</h2>
      <p class="hint">Pick the source face to swap into your target media.</p>
    </header>

    <section class="section">
      <div class="section-head">
        <h3>Source Face</h3>
        <span v-if="faces.length" class="count">{{ faces.length }}</span>
      </div>

      <div class="faces-row">
        <label class="face-add" :class="{ disabled: isUploadingFace }">
          <input
            type="file"
            accept="image/*"
            class="hidden"
            :disabled="isUploadingFace"
            @change="onAddFace"
          />
          <svg v-if="!isUploadingFace" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span v-else class="spinner-sm" />
          <span class="add-label">{{ isUploadingFace ? 'Uploading' : 'Add face' }}</span>
        </label>

        <button
          v-for="f in faces"
          :key="f.face_id"
          class="face-tile"
          :class="{ selected: selectedFaceId === f.face_id }"
          @click="pickFace(f.face_id)"
        >
          <img :src="f.url" :alt="`face-${f.face_id}`" loading="lazy" />
        </button>

        <p v-if="!faces.length && !isUploadingFace" class="faces-empty">
          No faces yet — add one to get started.
        </p>
      </div>
    </section>

    <footer class="cta-wrap">
      <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
      <p v-else-if="!selectedFaceId" class="bottom-hint">Pick a source face above.</p>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.panel {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-5 text-white flex flex-col gap-4 min-h-[600px];
}
.head h2 { @apply text-lg font-bold; }
.hint { @apply text-xs text-neutral-500 mt-1; }

.section { @apply flex flex-col gap-3; }
.section-head {
  @apply flex items-center justify-between;
  h3 { @apply text-sm font-semibold; }
  .count { @apply text-xs rounded-full bg-amber-500/15 text-amber-300 px-2 py-0.5; }
}

.faces-row {
  @apply flex flex-wrap gap-3;
}
.face-add {
  @apply flex flex-col items-center justify-center size-[80px] shrink-0 rounded-2xl
    border-2 border-dashed border-amber-500/40 text-amber-400/80
    cursor-pointer transition;
  background: rgba(249, 115, 22, 0.04);
  &:hover:not(.disabled) {
    @apply border-amber-500 text-amber-300;
    background: rgba(249, 115, 22, 0.08);
  }
  &.disabled { @apply opacity-50 cursor-wait; }
  svg { width: 1.25rem; height: 1.25rem; }
  .add-label { @apply text-[10px] mt-1 font-medium; }
}
.face-tile {
  @apply relative size-[80px] shrink-0 rounded-2xl overflow-hidden border-2 border-transparent
    transition cursor-pointer;
  img { @apply size-full object-cover; }
  &:hover { @apply border-amber-500/40; }
  &.selected {
    @apply border-amber-500;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18);
  }
}
.faces-empty { @apply text-xs text-neutral-500 self-center; }

.cta-wrap { @apply mt-auto pt-4 flex flex-col gap-2; }
.cta {
  @apply flex items-center justify-between gap-2 w-full rounded-full px-6 py-3 text-base
    font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
  box-shadow: 0 8px 24px -8px rgba(249,115,22,0.5);
  &:hover:not(:disabled) { transform: scale(1.01); }
}
.spark { @apply text-amber-200; }
.cta-text { @apply flex-1 text-left; }
.cost { @apply rounded-full bg-black/30 px-3 py-0.5 text-xs; }
.err { @apply text-sm text-red-400; }
.bottom-hint { @apply text-xs text-neutral-500; }

.spinner-sm {
  display: inline-block;
  width: 1rem; height: 1rem;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #f97316;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
